import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { ROUTES } from '@/lib/constants';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { LandingPage } from '@/pages/LandingPage';
import { TermsPage } from '@/pages/TermsPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { ProjectWizardPage } from '@/pages/ProjectWizardPage';
import { PipelinePage } from '@/pages/PipelinePage';
import { DeliveryPage } from '@/pages/DeliveryPage';
import { RequireAuth } from '@/components/auth/RequireAuth';

// The experimenter panel is a separate bundle: participants never pay for it.
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));

/**
 * AURA route table.
 *
 * Public:
 *   /            — landing
 *   /terms       — terms & conditions
 *   /privacy     — privacy policy
 *
 * Authed (require sign-in):
 *   /projects                       — projects list
 *   /projects/:projectId/wizard     — questionnaire + sketches
 *   /jobs/:jobId                    — agent pipeline + checkpoints
 *   /jobs/:jobId/delivery           — hi-fi gallery + zip download
 *
 * Experimenter (own password, lazy-loaded):
 *   /admin/*                        — see pages/admin/AdminPage.tsx
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path={ROUTES.home} element={<LandingPage />} />
      <Route path={ROUTES.terms} element={<TermsPage />} />
      <Route path={ROUTES.privacy} element={<PrivacyPage />} />

      {/* Authed */}
      <Route
        path={ROUTES.projects}
        element={
          <RequireAuth>
            <ProjectsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/projects/:projectId/wizard"
        element={
          <RequireAuth>
            <ProjectWizardPage />
          </RequireAuth>
        }
      />

      {/* Pipeline + delivery — keyed off jobId since one project can have
          one active job. */}
      <Route path="/jobs/:jobId" element={<PipelinePage />} />
      <Route path="/jobs/:jobId/delivery" element={<DeliveryPage />} />

      {/* Experimenter panel — nested routes live inside AdminPage. */}
      <Route
        path="/admin/*"
        element={
          <Suspense
            fallback={
              <div className="flex min-h-screen items-center justify-center">
                <LoadingSpinner label="Loading experimenter panel…" />
              </div>
            }
          >
            <AdminPage />
          </Suspense>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
    </Routes>
  );
}
